import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { TaxonomyCategory, TaxonomyUnit, TaxonomySet } from '../types/taxonomy';

interface TaxonomyState {
  // Computed final data (Defaults merged with Custom)
  sets: TaxonomySet[];
  categories: TaxonomyCategory[];
  units: TaxonomyUnit[];
  
  // Raw Custom DB data
  dbSets: TaxonomySet[];
  dbCategories: TaxonomyCategory[];
  dbUnits: TaxonomyUnit[];
  
  isLoading: boolean;
  _familyId: string | null;
  _realtimeChannel: any | null;

  // Lifecycle
  init: (familyId: string) => Promise<void>;
  teardown: () => void;
  refreshSets: () => Promise<void>;
  refreshCategories: () => Promise<void>;
  refreshUnits: () => Promise<void>;

  // Actions
  addSet: (set: Omit<TaxonomySet, 'id' | 'family_id'>) => Promise<void>;
  updateSet: (id: string, updates: Partial<TaxonomySet>) => Promise<void>;
  deleteSet: (id: string) => Promise<void>;

  addCategory: (category: Omit<TaxonomyCategory, 'id' | 'family_id'>) => Promise<string>;
  updateCategory: (id: string, updates: Partial<TaxonomyCategory>) => Promise<void>;
  deleteCategory: (id: string) => Promise<void>;
  
  addUnit: (unit: Omit<TaxonomyUnit, 'id' | 'family_id'>) => Promise<void>;
  updateUnit: (id: string, updates: Partial<TaxonomyUnit>) => Promise<void>;
  deleteUnit: (id: string) => Promise<void>;
}

export const DEFAULT_SETS: TaxonomySet[] = [
  { id: 'groceries', name: 'Groceries', icon: 'ShoppingCart', description: 'Standard supermarket aisles' },
  { id: 'camping', name: 'Camping & Outdoors', icon: 'Tent', description: 'Gear, tools, and supplies' },
  { id: 'wine', name: 'Wine Cellar', icon: 'Wine', description: 'Wine types and regions' },
  { id: 'vinyl', name: 'Vinyl Collection', icon: 'Disc', description: 'Music genres and formats' },
  { id: 'books', name: 'Book Library', icon: 'BookOpen', description: 'Fiction, non-fiction, and more' },
  { id: 'clothes', name: 'Clothing & Accessories', icon: 'Shirt', description: 'Men\'s and women\'s apparel' },
];

export const DEFAULT_CATEGORIES: TaxonomyCategory[] = [
  // ── GROCERIES ──────────────────────────────────────────────────────────
  { id: 'produce', setId: 'groceries', name: 'Produce', emoji: '🥦', color: '#22c55e', order: 1 },
  { id: 'dairy', setId: 'groceries', name: 'Dairy & Eggs', emoji: '🥛', color: '#3b82f6', order: 2 },
  { id: 'meat', setId: 'groceries', name: 'Meat', emoji: '🥩', color: '#ef4444', order: 3 },
  { id: 'seafood', setId: 'groceries', name: 'Seafood', emoji: '🐟', color: '#0ea5e9', order: 4 },
  { id: 'bakery', setId: 'groceries', name: 'Bakery', emoji: '🍞', color: '#f59e0b', order: 5 },
  { id: 'pantry', setId: 'groceries', name: 'Pantry', emoji: '🍝', color: '#8b5cf6', order: 6 },
  { id: 'snacks', setId: 'groceries', name: 'Snacks', emoji: '🥨', color: '#f97316', order: 10 },
  { id: 'frozen', setId: 'groceries', name: 'Frozen', emoji: '🥶', color: '#06b6d4', order: 11 },
  { id: 'beverages', setId: 'groceries', name: 'Beverages', emoji: '🧃', color: '#ec4899', order: 12 },
  { id: 'household', setId: 'groceries', name: 'Household', emoji: '🧹', color: '#64748b', order: 14 },

  // ── CAMPING ────────────────────────────────────────────────────────────
  { id: 'shelter', setId: 'camping', name: 'Shelter', emoji: '⛺', color: '#16a34a', order: 20 },
  { id: 'sleeping', setId: 'camping', name: 'Sleeping', emoji: '🛌', color: '#2563eb', order: 21 },
  { id: 'cooking', setId: 'camping', name: 'Cooking', emoji: '🍳', color: '#ea580c', order: 22 },
  { id: 'tools', setId: 'camping', name: 'Tools & Hardware', emoji: '⚒️', color: '#4b5563', order: 23 },
  { id: 'clothing-outdoors', setId: 'camping', name: 'Clothing', emoji: '🧥', color: '#7c3aed', order: 24 },
  { id: 'first-aid', setId: 'camping', name: 'First Aid', emoji: '🩹', color: '#dc2626', order: 25 },

  // ── WINE (Hierarchical: Type -> Varietal) ───────────────────────────────
  { id: 'wine-red', setId: 'wine', name: 'Red Wine', emoji: '🍷', color: '#7f1d1d', order: 1 },
  { id: 'wine-red-cab', setId: 'wine', parentId: 'wine-red', name: 'Cabernet Sauvignon', emoji: '🍷', color: '#450a0a', order: 1 },
  { id: 'wine-red-pinot', setId: 'wine', parentId: 'wine-red', name: 'Pinot Noir', emoji: '🍷', color: '#991b1b', order: 2 },
  { id: 'wine-red-merlot', setId: 'wine', parentId: 'wine-red', name: 'Merlot', emoji: '🍷', color: '#7f1d1d', order: 3 },
  { id: 'wine-red-syrah', setId: 'wine', parentId: 'wine-red', name: 'Syrah / Shiraz', emoji: '🍷', color: '#4c0519', order: 4 },
  { id: 'wine-red-malbec', setId: 'wine', parentId: 'wine-red', name: 'Malbec', emoji: '🍷', color: '#4a044e', order: 5 },
  { id: 'wine-red-blend', setId: 'wine', parentId: 'wine-red', name: 'Red Blend', emoji: '🍷', color: '#7f1d1d', order: 6 },
  
  { id: 'wine-white', setId: 'wine', name: 'White Wine', emoji: '🥂', color: '#fef3c7', order: 2 },
  { id: 'wine-white-chardonnay', setId: 'wine', parentId: 'wine-white', name: 'Chardonnay', emoji: '🥂', color: '#fde68a', order: 1 },
  { id: 'wine-white-sauvignon', setId: 'wine', parentId: 'wine-white', name: 'Sauvignon Blanc', emoji: '🥂', color: '#fef3c7', order: 2 },
  { id: 'wine-white-pinotgrigio', setId: 'wine', parentId: 'wine-white', name: 'Pinot Grigio / Gris', emoji: '🥂', color: '#fef3c7', order: 3 },
  { id: 'wine-white-riesling', setId: 'wine', parentId: 'wine-white', name: 'Riesling', emoji: '🥂', color: '#fef08a', order: 4 },
  { id: 'wine-white-blend', setId: 'wine', parentId: 'wine-white', name: 'White Blend', emoji: '🥂', color: '#fef3c7', order: 5 },
  
  { id: 'wine-rose', setId: 'wine', name: 'Rosé', emoji: '🌸', color: '#fda4af', order: 3 },
  
  { id: 'wine-sparkling', setId: 'wine', name: 'Sparkling', emoji: '🍾', color: '#fde047', order: 4 },
  { id: 'wine-sparkling-champagne', setId: 'wine', parentId: 'wine-sparkling', name: 'Champagne', emoji: '🍾', color: '#fde047', order: 1 },
  { id: 'wine-sparkling-prosecco', setId: 'wine', parentId: 'wine-sparkling', name: 'Prosecco', emoji: '🍾', color: '#fef08a', order: 2 },
  
  { id: 'wine-fortified', setId: 'wine', name: 'Dessert & Fortified', emoji: '🍯', color: '#d97706', order: 5 },
  { id: 'wine-fortified-port', setId: 'wine', parentId: 'wine-fortified', name: 'Port', emoji: '🍷', color: '#7c2d12', order: 1 },
  { id: 'wine-fortified-sherry', setId: 'wine', parentId: 'wine-fortified', name: 'Sherry', emoji: '🥃', color: '#b45309', order: 2 },

  // ── BOOKS (Standard Genres) ─────────────────────────────────────────────
  { id: 'books-fiction', setId: 'books', name: 'Fiction', emoji: '📚', color: '#3b82f6', order: 1 },
  { id: 'books-fiction-scifi', setId: 'books', parentId: 'books-fiction', name: 'Science Fiction', emoji: '👽', color: '#0ea5e9', order: 1 },
  { id: 'books-fiction-fantasy', setId: 'books', parentId: 'books-fiction', name: 'Fantasy', emoji: '🐉', color: '#8b5cf6', order: 2 },
  { id: 'books-fiction-thriller', setId: 'books', parentId: 'books-fiction', name: 'Thriller & Mystery', emoji: '🕵️‍♂️', color: '#1f2937', order: 3 },
  { id: 'books-fiction-romance', setId: 'books', parentId: 'books-fiction', name: 'Romance', emoji: '❤️', color: '#f43f5e', order: 4 },
  { id: 'books-fiction-historical', setId: 'books', parentId: 'books-fiction', name: 'Historical Fiction', emoji: '🕰️', color: '#d97706', order: 5 },
  
  { id: 'books-nonfiction', setId: 'books', name: 'Non-Fiction', emoji: '📘', color: '#059669', order: 2 },
  { id: 'books-nonfiction-biography', setId: 'books', parentId: 'books-nonfiction', name: 'Biography & Memoir', emoji: '👤', color: '#4f46e5', order: 1 },
  { id: 'books-nonfiction-selfhelp', setId: 'books', parentId: 'books-nonfiction', name: 'Self-Help', emoji: '🌱', color: '#10b981', order: 2 },
  { id: 'books-nonfiction-history', setId: 'books', parentId: 'books-nonfiction', name: 'History & Politics', emoji: '🏛️', color: '#b45309', order: 3 },
  { id: 'books-nonfiction-science', setId: 'books', parentId: 'books-nonfiction', name: 'Science & Tech', emoji: '🔬', color: '#2563eb', order: 4 },
  { id: 'books-nonfiction-cooking', setId: 'books', parentId: 'books-nonfiction', name: 'Cooking & Food', emoji: '🍳', color: '#ea580c', order: 5 },
  
  { id: 'books-childrens', setId: 'books', name: "Children's Books", emoji: '🧸', color: '#f59e0b', order: 3 },

  // ── VINYL ──────────────────────────────────────────────────────────────
  { id: 'rock', setId: 'vinyl', name: 'Rock', emoji: '🎸', color: '#1f2937', order: 40 },
  { id: 'jazz', setId: 'vinyl', name: 'Jazz', emoji: '🎷', color: '#92400e', order: 41 },
  { id: 'electronic', setId: 'vinyl', name: 'Electronic', emoji: '🎹', color: '#06b6d4', order: 42 },
  { id: 'hip-hop', setId: 'vinyl', name: 'Hip Hop', emoji: '🎤', color: '#f43f5e', order: 43 },

  // ── CLOTHES & ACCESSORIES ──────────────────────────────────────────────
  { id: 'clothes-apparel', setId: 'clothes', name: 'Clothing & Apparel', emoji: '👕', color: '#2563eb', order: 1 },
  { id: 'clothes-apparel-tops', setId: 'clothes', parentId: 'clothes-apparel', name: 'Tops & Shirts', emoji: '👕', color: '#2563eb', order: 1 },
  { id: 'clothes-apparel-bottoms', setId: 'clothes', parentId: 'clothes-apparel', name: 'Bottoms & Pants', emoji: '👖', color: '#3b82f6', order: 2 },
  { id: 'clothes-apparel-dresses', setId: 'clothes', parentId: 'clothes-apparel', name: 'Dresses & Skirts', emoji: '👗', color: '#f472b6', order: 3 },
  { id: 'clothes-apparel-outerwear', setId: 'clothes', parentId: 'clothes-apparel', name: 'Outerwear', emoji: '🧥', color: '#1e3a8a', order: 4 },
  { id: 'clothes-apparel-activewear', setId: 'clothes', parentId: 'clothes-apparel', name: 'Activewear', emoji: '🏃‍♂️', color: '#60a5fa', order: 5 },
  { id: 'clothes-apparel-accessories', setId: 'clothes', parentId: 'clothes-apparel', name: 'Accessories', emoji: '⌚', color: '#1e40af', order: 6 },
  { id: 'clothes-apparel-shoes', setId: 'clothes', parentId: 'clothes-apparel', name: 'Shoes', emoji: '👞', color: '#172554', order: 7 },
];

export const DEFAULT_UNITS: TaxonomyUnit[] = [
  { id: 'pcs', name: 'Piece', plural: 'Pieces' },
  { id: 'count', name: 'Count', plural: 'Count' },
  { id: 'loaf', name: 'Loaf', plural: 'Loaves', subunitId: 'slice' },
  { id: 'slice', name: 'Slice', plural: 'Slices' },
  { id: 'carton', name: 'Carton', plural: 'Cartons', subunitId: 'pcs' },
  { id: 'bottle', name: 'Bottle', plural: 'Bottles' },
  { id: 'can', name: 'Can', plural: 'Cans' },
  { id: 'pack', name: 'Pack', plural: 'Packs' },
  { id: 'bag', name: 'Bag', plural: 'Bags' },
  { id: 'box', name: 'Box', plural: 'Boxes' },
  { id: 'tub', name: 'Tub', plural: 'Tubs' },
  { id: 'jar', name: 'Jar', plural: 'Jars' },
  { id: 'bunch', name: 'Bunch', plural: 'Bunches' },
  { id: 'head', name: 'Head', plural: 'Heads' },
  { id: 'stalk', name: 'Stalk', plural: 'Stalks' },
  { id: 'ear', name: 'Ear', plural: 'Ears' },
  { id: 'root', name: 'Root', plural: 'Roots' },
  { id: 'block', name: 'Block', plural: 'Blocks' },
  { id: 'dozen', name: 'Dozen', plural: 'Dozen' },
  { id: 'roll', name: 'Roll', plural: 'Rolls' },
  { id: 'tube', name: 'Tube', plural: 'Tubes' },
  { id: 'stick', name: 'Stick', plural: 'Sticks' },
  { id: 'canister', name: 'Canister', plural: 'Canisters' },
  { id: 'bear', name: 'Bear', plural: 'Bears' },
  { id: 'bar', name: 'Bar', plural: 'Bars' },
  { id: 'case', name: 'Case', plural: 'Cases' },
  { id: 'kg', name: 'Kilogram', plural: 'Kilograms', subunitId: 'g' },
  { id: 'g', name: 'Gram', plural: 'Grams' },
  { id: 'L', name: 'Litre', plural: 'Litres', subunitId: 'mL' },
  { id: 'mL', name: 'Millilitre', plural: 'Millilitres' },
  { id: 'l', name: 'Litre', plural: 'Litres', subunitId: 'ml' },
  { id: 'ml', name: 'Millilitre', plural: 'Millilitres' },
  { id: 'oz', name: 'Ounce', plural: 'Ounces' },
  { id: 'lb', name: 'Pound', plural: 'Pounds' },
];

const mergeData = <T extends { id: string; isDeleted?: boolean }>(defaults: T[], custom: T[]): T[] => {
  const customMap = new Map(custom.map(c => [c.id, c]));
  // Start with defaults, overridden by custom if IDs match, and filter out soft-deleted items
  const merged = defaults
    .map(d => customMap.has(d.id) ? customMap.get(d.id)! : d)
    .filter(item => !item.isDeleted);
  // Add purely custom items that are not deleted
  const defaultIds = new Set(defaults.map(d => d.id));
  merged.push(...custom.filter(c => !defaultIds.has(c.id) && !c.isDeleted));
  return merged;
};

const ensureDefaultCategoryInDb = async (id: string, familyId: string, dbCategories: TaxonomyCategory[]): Promise<void> => {
  const isCustom = dbCategories.some(c => c.id === id);
  if (isCustom) return;

  const defaultItem = DEFAULT_CATEGORIES.find(c => c.id === id);
  if (!defaultItem) return;

  if (defaultItem.parentId) {
    await ensureDefaultCategoryInDb(defaultItem.parentId, familyId, dbCategories);
  }

  const { error } = await supabase.from('taxonomy_categories').insert({
    id: defaultItem.id,
    family_id: familyId,
    name: defaultItem.name,
    emoji: defaultItem.emoji,
    color: defaultItem.color,
    set_id: defaultItem.setId,
    parent_id: defaultItem.parentId ?? null,
    order: defaultItem.order ?? 1,
    code: defaultItem.code
  });
  if (error) {
    console.error('Failed to ensure default category in DB:', error);
  }
};

const ensureDefaultUnitInDb = async (id: string, familyId: string, dbUnits: TaxonomyUnit[]): Promise<void> => {
  const isCustom = dbUnits.some(u => u.id === id);
  if (isCustom) return;

  const defaultItem = DEFAULT_UNITS.find(u => u.id === id);
  if (!defaultItem) return;

  if (defaultItem.subunitId) {
    await ensureDefaultUnitInDb(defaultItem.subunitId, familyId, dbUnits);
  }

  const { error } = await supabase.from('taxonomy_units').insert({
    id: defaultItem.id,
    family_id: familyId,
    name: defaultItem.name,
    plural: defaultItem.plural,
    subunit_id: defaultItem.subunitId ?? null,
    multiplier: defaultItem.multiplier ?? null
  });
  if (error) {
    console.error('Failed to ensure default unit in DB:', error);
  }
};

export const useTaxonomyStore = create<TaxonomyState>((set, get) => ({
  sets: DEFAULT_SETS,
  categories: DEFAULT_CATEGORIES,
  units: DEFAULT_UNITS,
  
  dbSets: [],
  dbCategories: [],
  dbUnits: [],

  isLoading: false,
  _familyId: null,
  _realtimeChannel: null,

  init: async (familyId: string) => {
    if (get()._familyId === familyId) return;
    set({ _familyId: familyId, isLoading: true });

    try {
      await Promise.all([
        get().refreshSets(),
        get().refreshCategories(),
        get().refreshUnits()
      ]);

      const channel = supabase
        .channel(`taxonomy-family-${familyId}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'taxonomy_sets', filter: `family_id=eq.${familyId}` }, () => {
          get().refreshSets();
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'taxonomy_categories', filter: `family_id=eq.${familyId}` }, () => {
          get().refreshCategories();
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'taxonomy_units', filter: `family_id=eq.${familyId}` }, () => {
          get().refreshUnits();
        })
        .subscribe();

      set({ _realtimeChannel: channel });
    } catch (err) {
      console.error('Failed to init taxonomy store', err);
    } finally {
      set({ isLoading: false });
    }
  },

  teardown: () => {
    const { _realtimeChannel } = get();
    if (_realtimeChannel) supabase.removeChannel(_realtimeChannel);
    set({
      _familyId: null,
      _realtimeChannel: null,
      dbSets: [],
      dbCategories: [],
      dbUnits: [],
      sets: DEFAULT_SETS,
      categories: DEFAULT_CATEGORIES,
      units: DEFAULT_UNITS
    });
  },

  refreshSets: async () => {
    const familyId = get()._familyId;
    if (!familyId) return;
    const { data } = await supabase.from('taxonomy_sets').select('*').eq('family_id', familyId);
    if (data) {
      set({ dbSets: data, sets: mergeData(DEFAULT_SETS, data) });
    }
  },

  refreshCategories: async () => {
    const familyId = get()._familyId;
    if (!familyId) return;
    const { data, error } = await supabase.from('taxonomy_categories').select('*').eq('family_id', familyId);
    if (error) {
      console.error('Failed to refresh categories:', error);
      return;
    }
    if (data) {
      const mapped = data.map((c: any) => ({
        id: c.id,
        family_id: c.family_id,
        name: c.name,
        emoji: c.emoji,
        color: c.color,
        setId: c.set_id,
        parentId: c.parent_id || undefined,
        order: c.order,
        code: c.code,
        isDeleted: c.is_deleted || false,
        created_at: c.created_at,
        updated_at: c.updated_at,
        created_by: c.created_by,
      }));
      set({ dbCategories: mapped, categories: mergeData(DEFAULT_CATEGORIES, mapped) });
    }
  },

  refreshUnits: async () => {
    const familyId = get()._familyId;
    if (!familyId) return;
    const { data, error } = await supabase.from('taxonomy_units').select('*').eq('family_id', familyId);
    if (error) {
      console.error('Failed to refresh units:', error);
      return;
    }
    if (data) {
      const mapped = data.map((u: any) => ({
        id: u.id,
        family_id: u.family_id,
        name: u.name,
        plural: u.plural,
        subunitId: u.subunit_id || undefined,
        multiplier: u.multiplier ? Number(u.multiplier) : undefined,
        isDeleted: u.is_deleted || false,
        created_at: u.created_at,
        updated_at: u.updated_at,
        created_by: u.created_by,
      }));
      set({ dbUnits: mapped, units: mergeData(DEFAULT_UNITS, mapped) });
    }
  },

  addSet: async (newSet) => {
    const familyId = get()._familyId;
    if (!familyId) return;
    const id = crypto.randomUUID();
    const { error } = await supabase.from('taxonomy_sets').insert({ id, family_id: familyId, ...newSet });
    if (error) console.error('Failed to add set:', error);
    await get().refreshSets();
  },

  updateSet: async (id, updates) => {
    const familyId = get()._familyId;
    if (!familyId) return;
    
    // Check if modifying a default set not in DB yet
    const isCustom = get().dbSets.some(s => s.id === id);
    if (!isCustom) {
      const defaultItem = DEFAULT_SETS.find(s => s.id === id);
      if (defaultItem) {
        const { error } = await supabase.from('taxonomy_sets').insert({ ...defaultItem, family_id: familyId, ...updates });
        if (error) console.error('Failed to update default set:', error);
        await get().refreshSets();
        return;
      }
    }
    const { error } = await supabase.from('taxonomy_sets').update(updates).eq('id', id).eq('family_id', familyId);
    if (error) console.error('Failed to update set:', error);
    await get().refreshSets();
  },

  deleteSet: async (id) => {
    const familyId = get()._familyId;
    if (!familyId) return;
    const { error } = await supabase.from('taxonomy_sets').delete().eq('id', id).eq('family_id', familyId);
    if (error) console.error('Failed to delete set:', error);
    await get().refreshSets();
  },

  addCategory: async (cat) => {
    const familyId = get()._familyId;
    if (!familyId) return '';

    if (cat.parentId) {
      await ensureDefaultCategoryInDb(cat.parentId, familyId, get().dbCategories);
      await get().refreshCategories();
    }

    const id = crypto.randomUUID();
    const newOrder = get().categories.filter(c => c.setId === cat.setId).length + 1;
    const { error } = await supabase.from('taxonomy_categories').insert({
      id,
      family_id: familyId,
      name: cat.name,
      emoji: cat.emoji,
      color: cat.color,
      set_id: cat.setId,
      parent_id: cat.parentId ?? null,
      order: cat.order ?? newOrder,
      code: cat.code
    });
    if (error) console.error('Failed to add category:', error);
    await get().refreshCategories();
    return id;
  },

  updateCategory: async (id, updates) => {
    const familyId = get()._familyId;
    if (!familyId) return;

    if (updates.parentId) {
      await ensureDefaultCategoryInDb(updates.parentId, familyId, get().dbCategories);
      await get().refreshCategories();
    }
    
    const dbUpdates: any = {};
    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.emoji !== undefined) dbUpdates.emoji = updates.emoji;
    if (updates.color !== undefined) dbUpdates.color = updates.color;
    if (updates.setId !== undefined) dbUpdates.set_id = updates.setId;
    if (updates.parentId !== undefined) dbUpdates.parent_id = updates.parentId ?? null;
    if (updates.order !== undefined) dbUpdates.order = updates.order;
    if (updates.code !== undefined) dbUpdates.code = updates.code;

    const isCustom = get().dbCategories.some(c => c.id === id);
    if (!isCustom) {
      const defaultItem = DEFAULT_CATEGORIES.find(c => c.id === id);
      if (defaultItem) {
        const { error } = await supabase.from('taxonomy_categories').insert({
          id: defaultItem.id,
          family_id: familyId,
          name: defaultItem.name,
          emoji: defaultItem.emoji,
          color: defaultItem.color,
          set_id: defaultItem.setId,
          parent_id: defaultItem.parentId ?? null,
          order: defaultItem.order ?? 1,
          code: defaultItem.code,
          ...dbUpdates
        });
        if (error) console.error('Failed to update default category:', error);
        await get().refreshCategories();
        return;
      }
    }
    const { error } = await supabase.from('taxonomy_categories').update(dbUpdates).eq('id', id).eq('family_id', familyId);
    if (error) console.error('Failed to update category:', error);
    await get().refreshCategories();
  },

  deleteCategory: async (id) => {
    const familyId = get()._familyId;
    if (!familyId) return;

    const performDelete = async (catId: string): Promise<void> => {
      // Find children in computed categories to handle both default and custom children
      const children = get().categories.filter(c => c.parentId === catId);
      for (const child of children) {
        await performDelete(child.id);
      }

      const isCustom = get().dbCategories.some(c => c.id === catId);
      if (!isCustom) {
        const defaultItem = DEFAULT_CATEGORIES.find(c => c.id === catId);
        if (defaultItem) {
          const { error } = await supabase.from('taxonomy_categories').upsert({
            id: defaultItem.id,
            family_id: familyId,
            name: defaultItem.name,
            emoji: defaultItem.emoji,
            color: defaultItem.color,
            set_id: defaultItem.setId,
            parent_id: defaultItem.parentId ?? null,
            order: defaultItem.order ?? 1,
            code: defaultItem.code,
            is_deleted: true
          });
          if (error) console.error('Failed to soft delete default category:', error);
        }
      } else {
        const { error } = await supabase.from('taxonomy_categories').delete().eq('id', catId).eq('family_id', familyId);
        if (error) console.error('Failed to delete category:', error);
      }
    };

    await performDelete(id);
    await get().refreshCategories();
  },

  addUnit: async (unit) => {
    const familyId = get()._familyId;
    if (!familyId) return;

    if (unit.subunitId) {
      await ensureDefaultUnitInDb(unit.subunitId, familyId, get().dbUnits);
      await get().refreshUnits();
    }
    
    const id = crypto.randomUUID();
    const { error } = await supabase.from('taxonomy_units').insert({
      id,
      family_id: familyId,
      name: unit.name,
      plural: unit.plural,
      subunit_id: unit.subunitId ?? null,
      multiplier: unit.multiplier ?? null
    });
    if (error) console.error('Failed to add unit:', error);
    await get().refreshUnits();
  },

  updateUnit: async (id, updates) => {
    const familyId = get()._familyId;
    if (!familyId) return;

    if (updates.subunitId) {
      await ensureDefaultUnitInDb(updates.subunitId, familyId, get().dbUnits);
      await get().refreshUnits();
    }
    
    const dbUpdates: any = {};
    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.plural !== undefined) dbUpdates.plural = updates.plural;
    if (updates.subunitId !== undefined) dbUpdates.subunit_id = updates.subunitId ?? null;
    if (updates.multiplier !== undefined) dbUpdates.multiplier = updates.multiplier ?? null;

    const isCustom = get().dbUnits.some(u => u.id === id);
    if (!isCustom) {
      const defaultItem = DEFAULT_UNITS.find(u => u.id === id);
      if (defaultItem) {
        const { error } = await supabase.from('taxonomy_units').insert({
          id: defaultItem.id,
          family_id: familyId,
          name: defaultItem.name,
          plural: defaultItem.plural,
          subunit_id: defaultItem.subunitId ?? null,
          multiplier: defaultItem.multiplier ?? null,
          ...dbUpdates
        });
        if (error) console.error('Failed to update default unit:', error);
        await get().refreshUnits();
        return;
      }
    }
    const { error } = await supabase.from('taxonomy_units').update(dbUpdates).eq('id', id).eq('family_id', familyId);
    if (error) console.error('Failed to update unit:', error);
    await get().refreshUnits();
  },

  deleteUnit: async (id) => {
    const familyId = get()._familyId;
    if (!familyId) return;

    const isCustom = get().dbUnits.some(u => u.id === id);
    if (!isCustom) {
      const defaultItem = DEFAULT_UNITS.find(u => u.id === id);
      if (defaultItem) {
        const { error } = await supabase.from('taxonomy_units').upsert({
          id: defaultItem.id,
          family_id: familyId,
          name: defaultItem.name,
          plural: defaultItem.plural,
          subunit_id: defaultItem.subunitId ?? null,
          multiplier: defaultItem.multiplier ?? null,
          is_deleted: true
        });
        if (error) console.error('Failed to soft delete default unit:', error);
        await get().refreshUnits();
        return;
      }
    }

    const { error } = await supabase.from('taxonomy_units').delete().eq('id', id).eq('family_id', familyId);
    if (error) console.error('Failed to delete unit:', error);
    await get().refreshUnits();
  },
}));

